// src/pages/FinancialDashboard.jsx
import React, { useState, useEffect, useRef } from 'react'
import { Tabs, TabsContent } from '@/components/ui/tabs'
import { ChartColumnBig, ChartNoAxesCombined, FileSpreadsheet } from 'lucide-react'
import BalanceSheetPage from './Tabs/BalanceSheetPage'
import FinancialDashboardPage from './Tabs/FinancialDashboardPage'
import ForecastPage from './Tabs/ForeCastPage'
import DashboardControls from '@/components/common/DashboardControls'
import { usePermissions } from '@/context/PermissionContext'

/* ─── Scale Hook ─── */
const useScaleDown = (baseWidth = 1280) => {
  const [scale, setScale] = React.useState(1)
  React.useEffect(() => {
    const calc = () => {
      const w = window.innerWidth
      setScale(w < baseWidth ? w / baseWidth : 1)
    }
    calc()
    window.addEventListener('resize', calc)
    return () => window.removeEventListener('resize', calc)
  }, [baseWidth])
  return scale
}

const FinancialDashboard = () => {
  const { hasPermission } = usePermissions()
  const scale = useScaleDown(1280)

  const [selectedContract,      setSelectedContract]      = useState(null)
  const [selectedContractLabel, setSelectedContractLabel] = useState(null)
  const [selectedDisplay,       setSelectedDisplay]       = useState(null)
  const [selectedCurrency,      setSelectedCurrency]      = useState(null)
  const [selectedYear,          setSelectedYear]          = useState(null)
  const [selectedMonth,         setSelectedMonth]         = useState(null)
  const [isExporting,           setIsExporting]           = useState(false)

  const printTargetRef = useRef(null)

  // ── permKey: DashboardControls ko batao active tab ke liye
  // kaun se userContracts key se filter karna hai
  const tabs = [
    {
      value:      'view_pnl_dashboard',
      label:      'Profit & Loss',
      icon:       <ChartNoAxesCombined className="h-4 w-4" />,
      component:  FinancialDashboardPage,
      permission: 'view_pnl_dashboard',
      permKey:    'profit_&_loss',      // ← matches userContracts key
    },
    {
      value:      'view_balance_sheet',
      label:      'Balance Sheet',
      icon:       <FileSpreadsheet className="h-4 w-4" />,
      component:  BalanceSheetPage,
      permission: 'view_balance_sheet',
      permKey:    'balance_sheet',      // ← matches userContracts key
    },
    {
      value:      'view_cash_flow',
      label:      'Cash Flow',
      icon:       <ChartColumnBig className="h-4 w-4" />,
      component:  ForecastPage,
      permission: 'view_cash_flow',
      permKey:    'cashflow',           // ← matches userContracts key
    },
  ].filter(tab => hasPermission(tab.permission))

  const [activeTab, setActiveTab] = useState(tabs[0]?.value || null)

  useEffect(() => {
    if (tabs.length > 0 && (!activeTab || !tabs.find(t => t.value === activeTab))) {
      setActiveTab(tabs[0].value)
    }
  }, [tabs, activeTab])

  // Tab switch hone pe selected contract reset karo
  // (kyunki naye tab ke liye alag contracts hain)
  const handleTabChange = (newTab) => {
    setActiveTab(newTab)
    // setSelectedContract(null)
    // setSelectedContractLabel(null)
  }

  const activeTabLabel  = tabs.find(t => t.value === activeTab)?.label  ?? 'Report'
  const activeTabPermKey = tabs.find(t => t.value === activeTab)?.permKey ?? null

  if (tabs.length === 0) {
    return (
      <div className="w-full px-4 sm:px-6 lg:px-8 pt-3">
        <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
          <h3 className="text-lg font-semibold mb-2">Access Denied</h3>
          <p className="text-muted-foreground">You don't have permission to view any financial reports.</p>
        </div>
      </div>
    )
  }

  const sharedProps = {
    selectedContract,
    selectedContractLabel,
    selectedDisplay,
    selectedCurrency,
    selectedYear,
    selectedMonth,
    isExporting,
  }

  return (
    <div className="w-full">

      {/* ── Sticky controls bar ── */}
      <div className="sticky top-0 z-20 bg-muted/10 pb-2">
        <DashboardControls
          selectedContract={selectedContract}
          selectedDisplay={selectedDisplay}
          selectedCurrency={selectedCurrency}
          selectedYear={selectedYear}
          selectedMonth={selectedMonth}
          onContractChange={setSelectedContract}
          onContractLabelChange={setSelectedContractLabel}
          onDisplayChange={setSelectedDisplay}
          onCurrencyChange={setSelectedCurrency}
          onYearChange={setSelectedYear}
          onMonthChange={setSelectedMonth}
          activeTabLabel={activeTabLabel}
          activeTabPermKey={activeTabPermKey}
          printTargetRef={printTargetRef}
          onExportingChange={setIsExporting}
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />
      </div>

      {/* ── Page content ── */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <div style={{ width: '100%', overflowX: 'hidden' }}>
          <div
            ref={printTargetRef}
            style={{
              transform: `scale(${scale})`,
              transformOrigin: 'top left',
              width: scale < 1 ? `${(100 / scale).toFixed(2)}%` : '100%',
            }}
          >
            {tabs.map(tab => (
              <TabsContent key={tab.value} value={tab.value}>
                <tab.component {...sharedProps} />
              </TabsContent>
            ))}
          </div>
        </div>
      </Tabs>

    </div>
  )
}

export default FinancialDashboard